import { Event } from '../../../types/event';
import { EventType } from '../../../types/eventType';
import { Ticket, TicketDate } from '../../../types/ticket';

export type DataCarouselItem = {
  id: number;
  title: string;
  subtitle?: string;
  sessionTitle?: string;
  status?: string | string[];
  isPassport: boolean;
  days: string | null;
  weekAndHour: string | null;
  hasTicketAvailable: boolean;
  hasResale?: boolean;
  resaleCount?: number;
  groupsReference: number[];
  definitiveSessionId: string;
  finished: boolean;
  tickets?: Ticket[];
  callback: () => void;
};

export type FormatTicketsDeps = {
  sessionDetails: Pick<EventType, 'date'> | null;
  event: Pick<Event, 'timezone'> | null;
  hasSellableTicketType: (ticket: Ticket) => boolean;
  checksIfAllTicketsAreSoldOut: (args: { ticket: Ticket }) => boolean;
  formatDate: (datetime: string, timezone: string) => Date;
  formatTime: (adjustedDate: Date) => string;
  formatDateToWeekAndHour: (adjustedDate: Date) => string;
  formatDateWithLocale: (adjustedDate: Date) => string;
  extractDate: (formattedString: string) => string;
  getPassportLabel: () => string;
  selectIdDateCarousel: (id: number) => void;
};

// Defensive: API payloads de tickets/sessions já chegaram null/undefined em produção
// (Sentry ALCHEMIST-1P8), por isso os guards `|| []` abaixo são intencionais — sem eles
// o EventProvider crasha em race condition entre getGetTickets e getSessionDetails.
export function formatTickets(
  tickets: Ticket[],
  deps: FormatTicketsDeps
): DataCarouselItem[] {
  const formattedTickets: DataCarouselItem[] = [];
  const ticketsGroupedBySessionDate: (TicketDate & { tickets: Ticket[] })[] = [];
  const passportSessions: Ticket[] = [];

  for (const ticket of tickets) {
    if (ticket.isPassport) {
      passportSessions.push(ticket);
      continue;
    }
    const dateId = ticket.type[0].dates[0].id;
    const index = ticketsGroupedBySessionDate.findIndex(td => td.id == dateId);

    if (index == -1) {
      ticketsGroupedBySessionDate.push({ ...ticket.type[0].dates[0], tickets : [{ ...ticket }] });
      continue;
    }

    ticketsGroupedBySessionDate[index].tickets.push({ ...ticket });
  }

  if (passportSessions.length > 0) {
    const groupsReference: number[] = [];
    const hasTicketAvailable: boolean[] = [];
    const finished: boolean[] = [];

    for (const passport of passportSessions) {
      groupsReference.push(passport.id);

      hasTicketAvailable.push(deps.hasSellableTicketType(passport)
        && !deps.checksIfAllTicketsAreSoldOut({ ticket : passport }));

      finished.push(passport.status !== 'available');
    }

    const passportLabel = deps.getPassportLabel();

    formattedTickets.push({
      id : passportSessions[0]?.id,
      title : passportLabel,
      subtitle : undefined,
      hasTicketAvailable : hasTicketAvailable.some((v) => v),
      isPassport : true,
      status : passportSessions.map(t => t.status).join(', ') || 'unknown',
      finished : finished.every((v) => v),
      days : passportSessions[0].type[0].dates.map(date => date.date.substring(0, 5)).join(' - '),
      weekAndHour : null,
      groupsReference,
      definitiveSessionId : passportSessions[0].type[0].dates.map(date => date.id).join(''),
      callback : () => deps.selectIdDateCarousel(passportSessions[0].id)
    });
  }

  ticketsGroupedBySessionDate.sort((a, b) => a.datetime.localeCompare(b.datetime));

  for (const ticketGroup of ticketsGroupedBySessionDate) {
    const ticketGroupTickets = ticketGroup.tickets || [];
    const hasTicketAvailable = ticketGroupTickets
      .map(t => deps.hasSellableTicketType(t) && !deps.checksIfAllTicketsAreSoldOut({ ticket : t }))
      .some(v => v);

    const matchingDate = (deps.sessionDetails?.date || []).find(d => d.id === ticketGroup.id);
    const finished = deps.sessionDetails ? (matchingDate?.status !== 'available') : false;

    const adjustedDate = deps.formatDate(ticketGroup.datetime, deps.event?.timezone ?? 'America/Sao_Paulo');
    const formattedTime = deps.formatTime(adjustedDate);
    const formattedWeekAndHour = deps.formatDateToWeekAndHour(adjustedDate);
    const groupsReference = ticketGroupTickets.map((t) => t.id);
    const formatSessionTitle = deps.formatDateWithLocale(adjustedDate);

    formattedTickets.push({
      id : ticketGroup.id,
      title : deps.extractDate(formattedWeekAndHour),
      subtitle : formattedTime,
      sessionTitle : formatSessionTitle,
      hasTicketAvailable,
      isPassport : false,
      days : null,
      status : ticketGroupTickets.map(t => t.status).join(', ') || 'unknown',
      finished,
      weekAndHour : formattedWeekAndHour,
      groupsReference,
      definitiveSessionId : ticketGroup.id.toString(),
      callback : () => deps.selectIdDateCarousel(ticketGroup.id)
    });
  }

  formattedTickets.sort((a, b) => {
    if (a.hasTicketAvailable && !b.hasTicketAvailable) {
      return -1;
    }
    return 0;
  });

  return formattedTickets;
}
